import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  TheaterComedy as TheaterComedyIcon,
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../api_middleware";
import { EnrichedSession } from "./types";
import { buildResLinkUrl } from "./utils";
import { RootState, AppDispatch } from "../../../redux/store";
import { fetchOrganizations } from "../../../redux/actions/organizationActions";
import BetterstackLinksSection from "../schedule/BetterstackLinksSection";
import { generateBetterstackLinks } from "../schedule/utils";

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
};

const getStatusColor = (status: string): string => {
  const lowerStatus = status.toLowerCase();
  if (lowerStatus.includes("finished") || lowerStatus.includes("finalized")) {
    return "#4caf50";
  }
  if (lowerStatus.includes("running") || lowerStatus.includes("active")) {
    return "#2196f3";
  }
  if (lowerStatus.includes("waiting") || lowerStatus.includes("pending")) {
    return "#ff9800";
  }
  if (lowerStatus.includes("error") || lowerStatus.includes("failed")) {
    return "#f44336";
  }
  return "#9e9e9e";
};

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  copyValue?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, copyValue }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (copyValue) {
      navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: "#666", minWidth: 140 }}
      >
        {label}:
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ color: "#333" }}>
          {value}
        </Typography>
        {copyValue && (
          <Tooltip title={copied ? "Copied!" : "Copy"}>
            <IconButton size="small" onClick={handleCopy} sx={{ padding: 0.5 }}>
              {copied ? (
                <CheckCircleIcon sx={{ fontSize: 14, color: "#4caf50" }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const SessionDetailView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [session, setSession] = useState<EnrichedSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get organizations from Redux
  const { organizations: reduxOrganizations } = useSelector(
    (state: RootState) => state.organizations
  );

  // Create a lookup map for organization names
  const orgNameLookup: Record<string, string> = useMemo(() => {
    const lookup: Record<string, string> = {};
    reduxOrganizations.forEach((org) => {
      lookup[org.id] = org.name || org.id;
    });
    return lookup;
  }, [reduxOrganizations]);

  // Generate Betterstack log links from session state history
  const betterstackLinks = useMemo(() => {
    if (!session?.api_session) return [];
    const apiSession = session.api_session;
    return generateBetterstackLinks(
      apiSession.session_id,
      apiSession.conversation_id || null,
      apiSession.state_history || null,
      apiSession.log_session_id || null
    );
  }, [session?.api_session]);

  // Load organizations from Redux
  useEffect(() => {
    if (reduxOrganizations.length === 0) {
      dispatch(fetchOrganizations());
    }
  }, [dispatch, reduxOrganizations.length]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api(`v1/admin/pixels/sessions/${sessionId}`, "GET");
        setSession(res);
      } catch (err: any) {
        setError(err.message || "Failed to fetch session");
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/sessions")}
          sx={{ mt: 2 }}
        >
          Back to Sessions
        </Button>
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Session not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/sessions")}
          sx={{ mt: 2 }}
        >
          Back to Sessions
        </Button>
      </Box>
    );
  }

  // Extract nested data for easier access
  const apiSession = session.api_session;
  const experienceInfo = session.experience_info;

  const createdAt = new Date(apiSession.created_at);
  const sessionLifetime = (Date.now() - createdAt.getTime()) / 1000;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/dashboard/sessions")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Session Details
        </Typography>
        <Chip
          label={apiSession.status}
          size="small"
          sx={{
            backgroundColor: getStatusColor(apiSession.status),
            color: "#fff",
            fontWeight: 500,
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Session Info Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              title="Session Information"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              <InfoRow
                label="Session ID"
                value={
                  <Box
                    component="code"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      bgcolor: "#f5f5f5",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    {apiSession.session_id}
                  </Box>
                }
                copyValue={apiSession.session_id}
              />
              <InfoRow
                label="Status"
                value={
                  <Chip
                    label={apiSession.status}
                    size="small"
                    sx={{
                      backgroundColor: getStatusColor(apiSession.status),
                      color: "#fff",
                      fontSize: "0.75rem",
                    }}
                  />
                }
              />
              <InfoRow
                label="Created"
                value={format(createdAt, "yyyy-MM-dd HH:mm:ss")}
              />
              <InfoRow
                label="Session Lifetime"
                value={formatDuration(sessionLifetime)}
              />
              {apiSession.expire_after && (
                <InfoRow
                  label="Expires"
                  value={format(
                    new Date(apiSession.expire_after),
                    "yyyy-MM-dd HH:mm:ss"
                  )}
                />
              )}
              <InfoRow
                label="Connection"
                value={apiSession.connection_method}
              />
              {apiSession.telemetry?.negotiated_codec && (
                <InfoRow
                  label="Codec"
                  value={`${apiSession.telemetry.negotiated_codec.codec} (${apiSession.telemetry.negotiated_codec.negotiation_duration_sec?.toFixed(2)}s)`}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* User Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              avatar={<PersonIcon />}
              title="User Information"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              {apiSession.user_email ? (
                <>
                  <InfoRow
                    label="Email"
                    value={
                      apiSession.internal_user_id ? (
                        <Link
                          to={`/dashboard/users/${apiSession.internal_user_id}`}
                          style={{ color: "#1976d2", textDecoration: "none" }}
                        >
                          {apiSession.user_email}
                          <OpenInNewIcon
                            sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
                          />
                        </Link>
                      ) : (
                        apiSession.user_email
                      )
                    }
                    copyValue={apiSession.user_email}
                  />
                  {apiSession.internal_user_id && (
                    <InfoRow
                      label="User ID"
                      value={
                        <Box
                          component="code"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {apiSession.internal_user_id.substring(0, 8)}...
                        </Box>
                      }
                      copyValue={apiSession.internal_user_id}
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No user information available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Organization Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              avatar={<BusinessIcon />}
              title="Organization"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              <InfoRow
                label="Organization"
                value={
                  <Link
                    to={`/dashboard/organizations/${apiSession.organization_id}`}
                    style={{ color: "#1976d2", textDecoration: "none" }}
                  >
                    {orgNameLookup[apiSession.organization_id] || apiSession.organization_id.substring(0, 8)}
                    <OpenInNewIcon
                      sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
                    />
                  </Link>
                }
                copyValue={apiSession.organization_id}
              />
              <InfoRow
                label="Deployment"
                value={
                  <Box
                    component="code"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.8rem",
                      bgcolor: "#f5f5f5",
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    {apiSession.deployment_id.substring(0, 12)}...
                  </Box>
                }
                copyValue={apiSession.deployment_id}
              />
              {apiSession.build_id && (
                <InfoRow
                  label="Build"
                  value={
                    <Link
                      to={`/dashboard/builds/${apiSession.build_id}`}
                      style={{ color: "#1976d2", textDecoration: "none" }}
                    >
                      {apiSession.build_id.substring(0, 8)}...
                      <OpenInNewIcon
                        sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
                      />
                    </Link>
                  }
                  copyValue={apiSession.build_id}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Conversation Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              avatar={<ChatIcon />}
              title="Conversation"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              {apiSession.conversation_id ? (
                <>
                  <InfoRow
                    label="Conversation ID"
                    value={
                      <Link
                        to={`/dashboard/conversations/${apiSession.conversation_id}`}
                        style={{ color: "#1976d2", textDecoration: "none" }}
                      >
                        {apiSession.conversation_id.substring(0, 8)}...
                        <OpenInNewIcon
                          sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
                        />
                      </Link>
                    }
                    copyValue={apiSession.conversation_id}
                  />
                  {session.chat_session_id && (
                    <InfoRow
                      label="Chat Session ID"
                      value={
                        <Box
                          component="code"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {session.chat_session_id.substring(0, 8)}...
                        </Box>
                      }
                      copyValue={session.chat_session_id}
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No conversation linked
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Experience Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              avatar={
                experienceInfo?.experience_type === "assessment" ? (
                  <AssignmentIcon />
                ) : experienceInfo?.experience_type === "roleplay" ? (
                  <TheaterComedyIcon />
                ) : (
                  <AssignmentIcon />
                )
              }
              title="Experience"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              {experienceInfo?.experience_type ? (
                <>
                  <InfoRow
                    label="Type"
                    value={
                      <Chip
                        label={experienceInfo.experience_type === "assessment" ? "Assessment" : "Roleplay"}
                        size="small"
                        color={experienceInfo.experience_type === "assessment" ? "primary" : "secondary"}
                        sx={{ fontSize: "0.75rem" }}
                      />
                    }
                  />
                  <InfoRow
                    label="Name"
                    value={
                      experienceInfo.experience_id ? (
                        <Link
                          to={`/dashboard/${experienceInfo.experience_type === "assessment" ? "assessments" : "roleplays"}/${apiSession.organization_id}/${experienceInfo.experience_id}`}
                          style={{ color: "#1976d2", textDecoration: "none" }}
                        >
                          {experienceInfo.experience_name || experienceInfo.experience_key}
                          <OpenInNewIcon
                            sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle" }}
                          />
                        </Link>
                      ) : (
                        experienceInfo.experience_name || experienceInfo.experience_key || "-"
                      )
                    }
                  />
                  {experienceInfo.experience_key && (
                    <InfoRow
                      label="Key"
                      value={
                        <Box
                          component="code"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            bgcolor: "#f5f5f5",
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                          }}
                        >
                          {experienceInfo.experience_key}
                        </Box>
                      }
                      copyValue={experienceInfo.experience_key}
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No experience linked
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Artifact Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardHeader
              title="Artifact"
              titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
            />
            <CardContent>
              {experienceInfo?.has_artifact && experienceInfo.artifact_id ? (
                <>
                  <InfoRow
                    label="Artifact ID"
                    value={
                      <Box
                        component="code"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          bgcolor: "#f5f5f5",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                        }}
                      >
                        {experienceInfo.artifact_id.substring(0, 8)}...
                      </Box>
                    }
                    copyValue={experienceInfo.artifact_id}
                  />
                  {experienceInfo.artifact_state && (
                    <InfoRow label="State" value={experienceInfo.artifact_state} />
                  )}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="success"
                      size="small"
                      onClick={() => {
                        const path =
                          experienceInfo.experience_type === "assessment"
                            ? `/dashboard/assessments/${experienceInfo.experience_id}/sessions/${experienceInfo.artifact_id}?org=${apiSession.organization_id}`
                            : `/dashboard/roleplays/${apiSession.organization_id}/${experienceInfo.experience_id}/sessions/${experienceInfo.artifact_id}`;
                        navigate(path);
                      }}
                    >
                      View Artifact
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No artifact available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* State History Card */}
        {apiSession.state_history && apiSession.state_history.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardHeader
                title="State History"
                titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
              />
              <CardContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {apiSession.state_history.map((state, index) => {
                    const stateTime = new Date(state.entered_at);
                    const elapsed = (stateTime.getTime() - createdAt.getTime()) / 1000;
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          p: 1,
                          bgcolor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", color: "#666", minWidth: 80 }}
                        >
                          +{formatDuration(elapsed)}
                        </Typography>
                        <Chip
                          label={state.data?.status || "unknown"}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(state.data?.status || ""),
                            color: "#fff",
                            fontSize: "0.7rem",
                            minWidth: 100,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {format(stateTime, "HH:mm:ss")}
                        </Typography>
                        {state.data?.node_id && (
                          <Typography variant="body2" sx={{ color: "#888" }}>
                            Node: {state.data.node_id}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Debug Info */}
        {(apiSession.log_session_id || betterstackLinks.length > 0) && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader
                title="Debug Information"
                titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
              />
              <CardContent>
                {apiSession.log_session_id && (
                  <InfoRow
                    label="Log Session ID"
                    value={
                      <Box
                        component="code"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          bgcolor: "#f5f5f5",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                        }}
                      >
                        {apiSession.log_session_id}
                      </Box>
                    }
                    copyValue={apiSession.log_session_id}
                  />
                )}
                {betterstackLinks.length > 0 && (
                  <BetterstackLinksSection links={betterstackLinks} />
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Launch Link Card */}
        {session.launch_args_id && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardHeader
                avatar={<LinkIcon />}
                title="Launch Link"
                titleTypographyProps={{ variant: "h6", fontWeight: 500 }}
              />
              <CardContent>
                <InfoRow
                  label="Launch Args ID"
                  value={
                    <Box
                      component="code"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        bgcolor: "#f5f5f5",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                      }}
                    >
                      {session.launch_args_id}
                    </Box>
                  }
                  copyValue={session.launch_args_id}
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        buildResLinkUrl(session.launch_args_id!, apiSession.organization_id, apiSession.connection_method)
                      );
                    }}
                  >
                    Copy URL
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Back Button */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/dashboard/sessions")}
        >
          Back to Sessions
        </Button>
      </Box>
    </Box>
  );
};

export default SessionDetailView;
